/* eslint-disable import/no-cycle */
import axios from 'axios';

import { store } from '../../index';
import { startHeartbeat, processHeartbeat } from '../../redux/epics/uploadHeartbeat/uploadHeartbeat';
import {
  updateFileStatus,
  updateFileError,
  updateFileUploadProgress,
  abortUpload,
  setUploadInfo,
  setRetryStatus,
} from '../../redux/files/actionCreators';
import { logViaChillipharm } from './log';

export const logError = (errorInfo) => {
  logViaChillipharm('error', errorInfo);
};

export const errorFormatter = (error, logMessage, file) => {
  let errorMessage;
  if (!error.response) {
    errorMessage = 'Network Error';
  } else if (error.message) {
    errorMessage = error.message;
  } else if (error.response.message) {
    errorMessage = error.response.message;
  } else if (error.response.statusText) {
    errorMessage = error.response.statusText;
  }

  return {
    log_message: logMessage,
    file_name: file.originalFileData.name,
    file_size: file.originalFileData.size,
    error: errorMessage,
  };
};

export function handleError(error, fileIndex, apiResponseError) {
  let errorMessage;
  let errorCode;

  if (!error) return;

  if (apiResponseError) {
    errorMessage = error.message ? error.message : 'Unknown error';
    errorCode = error.code ? error.code : 'Unknown code';
  } else {
    if (!error.response) {
      errorMessage = 'Network Error';
      errorCode = 'NetworkError';
    } else {
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response.message) {
        errorMessage = error.response.message;
      } else if (error.response.statusText) {
        errorMessage = error.response.statusText;
      }

      errorCode = error.response.code ? error.response.code : error.response.status;
    }

    if (store.getState().files.files[fileIndex].abortUpload) {
      errorMessage = 'Upload aborted by user';
      errorCode = 'RequestAbortedError';
    }
  }

  uploadCallback({ code: errorCode, message: errorMessage }, fileIndex);
}

// Handle upload success or error
export function uploadCallback(err, fileIndex) {
  if (err) {
    store.dispatch(updateFileStatus(fileIndex, 'Error'));
    store.dispatch(updateFileError(fileIndex, { code: err.code, message: err.message }));
    processHeartbeat(fileIndex);
  } else {
    store.dispatch(updateFileStatus(fileIndex, 'Uploaded'));
    store.dispatch(updateFileStatus(fileIndex, 'Success'));
    processHeartbeat(fileIndex);
  }
}

// Return the correct endpoint to post to in Chillipharm
// Needs to provide collection id if available
export function createChillipharmUrl(urlType) {
  const { currentLibraryId } = store.getState().internal;
  let url;

  switch (urlType) {
    case 'heartbeat':
      url = `/libraries/${currentLibraryId}/uploads/${urlType}`;
      break;
    case 'start_multipart':
      url = `/libraries/${currentLibraryId}/uploads/${urlType}`;
      break;
    case 'upload_part':
      url = `/libraries/${currentLibraryId}/uploads/${urlType}`;
      break;
    case 'complete_multipart':
      url = `/libraries/${currentLibraryId}/uploads/${urlType}`;
      break;
    case 'abort_multipart':
      url = `/libraries/${currentLibraryId}/uploads/${urlType}`;
      break;
    default:
      url = '';
  }

  return url;
}

export const isDeviceOnline = () => {
  return window.navigator.onLine;
};

export function handleFailedPart(fileUploadData, partIndex, error) {
  const { file } = fileUploadData;
  let offlineRetryAttempts = 0;

  const reTryInterval = setInterval(() => {
    if (isDeviceOnline()) {
      clearInterval(reTryInterval);
      retryFailedPart(fileUploadData, partIndex, error);
    } else {
      offlineRetryAttempts += 1;
      if (offlineRetryAttempts > 4) {
        handleError(error, file.index, false);
        clearInterval(reTryInterval);
      }
    }
  }, 10000);
}

export const startUpload = async (file) => {
  try {
    const startMultipartResponse = await axios.get(createChillipharmUrl('start_multipart'), {
      params: {
        filename: file.originalFileData.name,
        filesize: file.originalFileData.size,
        mime_type: file.originalFileData.type,
      },
      headers: { Accept: 'application/json' },
    });

    const { upload_id: uploadID, status, error } = startMultipartResponse.data;

    if (status === 'error' || !uploadID) {
      handleError(error, file.index, true);
      logError(errorFormatter(error, 'Error requesting start_multipart', file));
    } else {
      prepareMultipartFileUpload(calculateMultipartFileUpload(file, uploadID));
    }
  } catch (error) {
    handleError(error, file.index, false);
    logError(errorFormatter(error, 'Error requesting start_multipart', file));
  }
};

export function prepareMultipartFileUpload(fileUploadData) {
  const { currentCollectionId } = store.getState().internal;
  const { file, uploadId, uploadChunks } = fileUploadData;
  store.dispatch(
    setUploadInfo(file.index, {
      uploadId: uploadId,
      totalPartNumbers: uploadChunks,
      totalUploadedBytes: 0,
      collectionId: currentCollectionId,
    }),
  );
  uploadMultipartFile(fileUploadData);
}

export function calculateMultipartFileUpload(file, uploadId, fileChunkSize = 10000000) {
  const concurrentUploadParts = 1;
  const fileSize = file.originalFileData.size;
  const chunksRemainder = !!(fileSize % fileChunkSize);
  const uploadChunksFloor = Math.floor(fileSize / fileChunkSize);
  let uploadChunks;
  if (uploadChunksFloor === 0) {
    uploadChunks = 1;
  } else {
    uploadChunks = chunksRemainder ? uploadChunksFloor + 1 : uploadChunksFloor;
  }
  const chunkBatchsRemainder = !!(uploadChunks % concurrentUploadParts);
  const chunkBatchsFloor = Math.floor(uploadChunks / concurrentUploadParts);
  let chunkBatchs;
  if (chunkBatchsFloor === 0) {
    chunkBatchs = 1;
  } else if (chunkBatchsRemainder) {
    chunkBatchs = chunkBatchsFloor + 1;
  } else {
    chunkBatchs = chunkBatchsFloor;
  }
  const originalChunkBatchs = chunkBatchs;
  const remainingChunks = uploadChunks % concurrentUploadParts === 0 ? 1 : uploadChunks % concurrentUploadParts;
  const fileUploadData = {
    uploadId: uploadId,
    uploadChunks: uploadChunks,
    file: file,
    fileChunkSize: fileChunkSize,
    chunkBatchs: chunkBatchs,
    originalChunkBatchs: originalChunkBatchs,
    concurrentUploadParts: concurrentUploadParts,
    remainingChunks: remainingChunks,
    fileSize: fileSize,
    fileProgress: {},
    uploadedBytes: {},
    promisesPartUploads: [],
    resolvedUrls: [],
    retryAttempt: { partIndex: 0, retry: 0 },
    authenticityToken: store.getState().internal.authenticityToken,
  };
  return fileUploadData;
}

export async function uploadMultipartFile(fileUploadData, partIndex = 0) {
  let isPartError = false;
  let latestPartIndex = partIndex;
  const { file, promisesPartUploads, chunkBatchs, concurrentUploadParts, remainingChunks } = fileUploadData;

  try {
    // eslint-disable-next-line no-restricted-syntax
    for (const index of [...Array(chunkBatchs).keys()]) {
      const uploadChunksGroup = index === chunkBatchs - 1 ? remainingChunks : concurrentUploadParts;
      const { activateRetry } = store.getState().files.files[file.index];
      const fileStatus = store.getState().files.files[file.index].status;

      if (activateRetry || fileStatus === 'Error') {
        break;
      }

      latestPartIndex += uploadChunksGroup;
      uploadFileChunks(fileUploadData, uploadChunksGroup, latestPartIndex, isPartError);
      // eslint-disable-next-line no-await-in-loop
      isPartError = await resolveUploadParts(fileUploadData);
      promisesPartUploads.splice(0, promisesPartUploads.length);
      if (!isDeviceOnline()) {
        store.dispatch(setRetryStatus(file.index, true));
        break;
      }
    }

    if (store.getState().files.files[file.index].activateRetry) {
      if (!isDeviceOnline()) {
        handleFailedPart(fileUploadData, latestPartIndex, true);
      }
    } else {
      completeUpload(fileUploadData);
    }
  } catch (error) {
    handleError(error, file.index, false);
  }
}

export function uploadFileChunks(fileUploadData, uploadChunksGroup, partIndex, isPartError) {
  let start;
  let end;
  let blob;
  let latestPartIndex = partIndex;
  const { file, fileChunkSize, uploadChunks, promisesPartUploads } = fileUploadData;
  [...Array(uploadChunksGroup).keys()].forEach(() => {
    if (store.getState().files.files[file.index].abortUpload || isPartError) {
      return;
    }
    start = (latestPartIndex - 1) * fileChunkSize;
    end = latestPartIndex * fileChunkSize;
    blob =
      latestPartIndex < uploadChunks ? file.originalFileData.slice(start, end) : file.originalFileData.slice(start);
    const filePartResponse = postFilePart(fileUploadData, blob, latestPartIndex);
    if (!filePartResponse) return;
    promisesPartUploads.push(filePartResponse);
    latestPartIndex += 1;
  });
}

export async function resolveUploadParts(fileUploadData) {
  const { promisesPartUploads, file, resolvedUrls } = fileUploadData;
  let isPartError = false;

  try {
    await Promise.all(promisesPartUploads).then((resolvedUpload) => {
      const { status, error } = resolvedUpload[0] ? resolvedUpload[0].data : {};
      if (status === 'error') {
        isPartError = true;
        handleError(error, file.index, true);
        return isPartError;
      }

      return resolvedUrls.push(...resolvedUpload);
    });
  } catch (error) {
    handleError(error, file.index, false);
    logError(errorFormatter(error, 'Error requesting upload_part', file));
    return true;
  }
  return isPartError;
}

export async function postFilePart(fileUploadData, blob, partIndex, cancelUpload = createCancelToken()) {
  const { file } = fileUploadData;

  let uploadPostResponse;
  try {
    uploadPostResponse = await axios.post(
      createChillipharmUrl('upload_part'),
      generateFileFormdata(partIndex, blob, fileUploadData),
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json',
        },
        cancelToken: cancelUpload.token,
        onUploadProgress: (progressEvent) => {
          const { percent, totalUploadedBytes } = calcProgress(progressEvent, fileUploadData, partIndex);
          updateUploadProgress(fileUploadData, percent, totalUploadedBytes);
          cancelPartUpload(file.index, cancelUpload);
        },
      },
    );
  } catch (error) {
    store.dispatch(setRetryStatus(file.index, true));
    handleFailedPart(fileUploadData, partIndex, error);
    logError(errorFormatter(error, `Error requesting upload_part ${partIndex}`, file));
    return false;
  }

  if (!uploadPostResponse?.data?.etag) {
    store.dispatch(setRetryStatus(file.index, true));
    handleFailedPart(fileUploadData, partIndex, uploadPostResponse);
    logError(errorFormatter(uploadPostResponse, `Error requesting upload_part ${partIndex}`, file));
    return false;
  }

  return uploadPostResponse;
}

export function generateFileFormdata(partIndex, blob, fileUploadData) {
  const formData = new FormData();
  const { uploadId, authenticityToken } = fileUploadData;
  formData.append('part_number', partIndex);
  formData.append('upload_id', uploadId);
  formData.append('data', blob);
  formData.append('authenticity_token', authenticityToken);

  return formData;
}

export async function completeUpload(fileUploadData) {
  const { resolvedUrls, uploadId, file, authenticityToken } = fileUploadData;
  const uploadPartsArray = [];
  const fileStatus = store.getState().files.files[file.index].status;
  const { currentCollectionId } = store.getState().internal;

  resolvedUrls.forEach((resolvedPromise, index) => {
    uploadPartsArray.push({
      part_number: index + 1,
      etag: resolvedPromise.data.etag,
    });
  });

  if (store.getState().files.files[file.index].abortUpload || fileStatus === 'Error') {
    return;
  }

  try {
    const completeMultiPart = await axios.post(
      createChillipharmUrl('complete_multipart'),
      {
        upload_id: uploadId,
        parts: uploadPartsArray,
        asset_params: {
          asset_infos_attributes: file.values.map((v) => {
            return {
              info_field_id: v.field.id.toString(),
              value: v.value,
            };
          }),
        },
        collection_id: currentCollectionId,
        authenticity_token: authenticityToken,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      },
    );

    const { status, error } = completeMultiPart.data;

    if (status === 'error') {
      handleError(error, file.index, true);
      return;
    }

    store.dispatch(updateFileUploadProgress(file.index, 100));
    uploadCallback(false, file.index);
  } catch (error) {
    handleError(error, file.index, false);
    logError(errorFormatter(error, 'Error requesting complete_multipart', file));
  }
}

export function calcProgress(progressEvent, fileUploadData, partIndex) {
  const { fileProgress, fileSize, uploadedBytes, uploadChunks } = fileUploadData;
  fileProgress[partIndex] = (progressEvent.loaded * 100) / progressEvent.total;
  uploadedBytes[partIndex] = progressEvent.loaded;
  const totalPercent = fileProgress ? Object.values(fileProgress).reduce((sum, num) => sum + num, 0) : 0;
  const totalLoadedBytes = uploadedBytes ? Object.values(uploadedBytes).reduce((sum, num) => sum + num, 0) : 0;
  const totalUploadedBytes = totalLoadedBytes > fileSize ? fileSize : totalLoadedBytes;
  let percent = parseInt(Math.round(totalPercent / uploadChunks), 10);

  if (percent === 100) {
    percent = 99;
  }
  return { percent, totalUploadedBytes };
}

export function updateUploadProgress(fileUploadData, percent, totalUploadedBytes) {
  const { file, uploadId, uploadChunks } = fileUploadData;
  store.dispatch(updateFileUploadProgress(file.index, percent));
  store.dispatch(
    setUploadInfo(file.index, {
      uploadId: uploadId,
      totalPartNumbers: uploadChunks,
      totalUploadedBytes: totalUploadedBytes,
    }),
  );
}

export function cancelPartUpload(fileIndex, cancelUpload) {
  const requestAbortErrorMsg = 'Upload aborted by user';
  if (store.getState().files.files[fileIndex].abortUpload) {
    cancelUpload.cancel(requestAbortErrorMsg);
  }
}

// Kick off all the internal processes to upload one or multiple files
// The relevant fileIndexes to upload should be passed in an array as the argument
export function commenceFilesUpload(fileIndices) {
  store.getState().files.files.forEach((file, fileIndex) => {
    if (!fileIndices.includes(fileIndex)) {
      return;
    }
    store.dispatch(updateFileStatus(fileIndex, 'Uploading'));
    startHeartbeat(fileIndex);
    startUpload(file);
  });
}

export function createCancelToken() {
  return axios.CancelToken.source();
}

export const abortFileUpload = (file, handleAbortError) => {
  const { uploadId } = store.getState().files.files[file.index].uploadInfo;
  store.dispatch(abortUpload(file.index, true));
  handleAbortError({}, file.index, false);

  if (uploadId) {
    axios
      .delete(createChillipharmUrl('abort_multipart'), {
        params: {
          upload_id: uploadId,
          authenticity_token: store.getState().internal.authenticityToken,
        },
        headers: {
          Accept: 'application/json',
        },
      })
      .catch((error) => {
        logError(errorFormatter(error, 'Error requesting abort_multipart', file));
      });
  }
};

export function retryFailedPart(fileUploadData, partIndex, error) {
  const { file, resolvedUrls, retryAttempt, originalChunkBatchs } = fileUploadData;

  if (retryAttempt.partIndex !== partIndex) {
    retryAttempt.retry = 0;
    retryAttempt.partIndex = partIndex;
  }

  if (retryAttempt.retry < 5) {
    retryAttempt.retry += 1;
    if (store.getState().files.files[file.index].activateRetry) {
      const recalculatedPart = partIndex - 1 > 0 ? partIndex - 1 : 0;

      if (partIndex - 1 > 0) {
        // eslint-disable-next-line no-param-reassign
        fileUploadData.chunkBatchs = originalChunkBatchs - (partIndex - 1);
      }

      resolvedUrls.pop();
      store.dispatch(setRetryStatus(file.index, false));
      uploadMultipartFile(fileUploadData, recalculatedPart);
    }
  } else {
    retryAttempt.retry = 0;
    handleError(error, file.index, false);
  }
}
